/**
 * types.ts
 * =============================================================================
 * Shared type definitions for the Neuro-Audio Lab audio core.
 * 
 * @author Neuro-Audio Lab
 * @version 1.0.0
 */

// =============================================================================
// AUDIO MODES
// =============================================================================

/**
 * The three main audio modes corresponding to product modules
 */
export type AudioMode = 'focus' | 'sleep' | 'anxiety';

/**
 * Display names for each mode (Spanish localization)
 */
export const AUDIO_MODE_LABELS: Record<AudioMode, string> = {
  focus: 'Enfoque Profundo',
  sleep: 'Sueño Reparador',
  anxiety: 'Reset de Ansiedad',
};

/**
 * Descriptions for each mode
 */
export const AUDIO_MODE_DESCRIPTIONS: Record<AudioMode, string> = {
  focus: 'Ruido marrón orgánico + pulsos Beta para concentración sostenida',
  sleep: 'Paisajes sonoros de Ecuador + pulsos Delta para sueño profundo',
  anxiety: 'Estimulación bilateral + pulsos Alpha para regulación emocional',
};

/**
 * Icons for each mode (emoji for MVP, replace with SVG later)
 */
export const AUDIO_MODE_ICONS: Record<AudioMode, string> = {
  focus: '🎯',
  sleep: '🌙',
  anxiety: '🧘',
};

// =============================================================================
// TRACK TYPES
// =============================================================================

/**
 * Track identifiers for the mixer
 */
export type TrackId = 'ambience' | 'tone' | 'voice';

/**
 * Display names for tracks
 */
export const TRACK_LABELS: Record<TrackId, string> = {
  ambience: 'Ambiente',
  tone: 'Tono Binaural',
  voice: 'Voz Guía',
};

/**
 * Track configuration
 */
export interface TrackConfig {
  id: TrackId;
  label: string;
  defaultVolume: number;
  canLoop: boolean;
}

/**
 * Default track configurations
 */
export const TRACK_CONFIGS: Record<TrackId, TrackConfig> = {
  ambience: {
    id: 'ambience',
    label: 'Ambiente',
    defaultVolume: 0.7,
    canLoop: true,
  },
  tone: {
    id: 'tone',
    label: 'Tono Binaural',
    defaultVolume: 0.3,
    canLoop: true, // Oscillators run continuously
  },
  voice: {
    id: 'voice',
    label: 'Voz Guía',
    defaultVolume: 0.9,
    canLoop: false,
  },
};

// =============================================================================
// BINAURAL FREQUENCIES
// =============================================================================

/**
 * Brainwave frequency ranges in Hz
 */
export const BRAINWAVE_RANGES = {
  delta: { min: 0.5, max: 4, label: 'Delta (Sueño profundo)' },
  theta: { min: 4, max: 8, label: 'Theta (Meditación)' },
  alpha: { min: 8, max: 12, label: 'Alpha (Relajación)' },
  beta: { min: 12, max: 30, label: 'Beta (Concentración)' },
  gamma: { min: 30, max: 100, label: 'Gamma (Alta cognición)' },
} as const;

/**
 * Binaural beat configuration
 */
export interface BinauralConfig {
  carrierFrequency: number;
  beatFrequency: number;
  waveform: OscillatorType;
}

/**
 * Default binaural configurations per mode
 */
export const DEFAULT_BINAURAL_CONFIGS: Record<AudioMode, BinauralConfig> = {
  focus: {
    carrierFrequency: 200,
    beatFrequency: 16, // Beta range
    waveform: 'sine',
  },
  sleep: {
    carrierFrequency: 150,
    beatFrequency: 2, // Delta range
    waveform: 'sine',
  },
  anxiety: {
    carrierFrequency: 180,
    beatFrequency: 10, // Alpha range
    waveform: 'sine',
  },
};

// =============================================================================
// TIMER TYPES
// =============================================================================

/**
 * Timer profile identifiers
 */
export type TimerProfileId = 'free' | 'pomodoro' | 'ultradian' | 'sleep_timer' | 'reset_timer';

/**
 * Timer state machine states
 */
export type TimerState = 'idle' | 'running' | 'paused' | 'break';

/**
 * Pomodoro cycle phase.
 * 'work' = active focus block, 'break' = rest block, 'none' = not in Pomodoro mode.
 */
export type PomodoroPhase = 'work' | 'break' | 'none';

/**
 * Timer profile configuration
 */
export interface TimerProfile {
  id: TimerProfileId;
  name: string;
  type: 'countup' | 'countdown'; // 'free' is countup, others countdown
  duration: number; // Seconds. 0 for 'free'. e.g., 25*60 for pomodoro
  breakDuration?: number; // Seconds. e.g., 5*60 for pomodoro. null for free/sleep
}

/**
 * Default timer profiles
 */
export const TIMER_PROFILES: Record<TimerProfileId, TimerProfile> = {
  free: {
    id: 'free',
    name: 'Libre',
    type: 'countup',
    duration: 0, // Infinite countup
    breakDuration: undefined,
  },
  pomodoro: {
    id: 'pomodoro',
    name: 'Pomodoro',
    type: 'countdown',
    duration: 25 * 60, // 25 minutes
    breakDuration: 5 * 60, // 5 minutes break
  },
  ultradian: {
    id: 'ultradian',
    name: 'Ultradiano',
    type: 'countdown',
    duration: 90 * 60, // 90 minutes
    breakDuration: 20 * 60, // 20 minutes break
  },
  sleep_timer: {
    id: 'sleep_timer',
    name: 'Temporizador de Sueño',
    type: 'countdown',
    duration: 45 * 60, // 45 minutes default
    breakDuration: undefined, // No break for sleep timer
  },
  reset_timer: {
    id: 'reset_timer',
    name: 'Reset Express',
    type: 'countdown',
    duration: 15 * 60, // 15 minutes (regulación del sistema nervioso)
    breakDuration: undefined, // No break for reset timer
  },
};

// Legacy exports for backward compatibility (deprecated)
/**
 * @deprecated Use TimerProfileId instead
 */
export type TimerMode = 'pomodoro' | 'ultradian' | 'infinite' | 'custom';

/**
 * @deprecated Use TIMER_PROFILES instead
 */
export const TIMER_PRESETS: Record<Exclude<TimerMode, 'infinite' | 'custom'>, number> = {
  pomodoro: 25,
  ultradian: 90,
};

/**
 * @deprecated Use TIMER_PROFILES instead
 */
export const TIMER_LABELS: Record<TimerMode, string> = {
  pomodoro: 'Pomodoro (25 min)',
  ultradian: 'Ciclo Ultradiano (90 min)',
  infinite: 'Sin límite',
  custom: 'Personalizado',
};

// =============================================================================
// AUDIO FILE TYPES
// =============================================================================

/**
 * Supported audio file formats
 */
export type AudioFormat = 'ogg' | 'mp3' | 'wav' | 'm4a';

/**
 * Audio file metadata
 */
export interface AudioFileMetadata {
  id: string;
  name: string;
  description?: string;
  duration: number; // in seconds
  format: AudioFormat;
  url: string;
  category: 'ambience' | 'voice';
  mode?: AudioMode;
  tags?: string[];
  location?: string; // Geographic location (for Ecuador field recordings)
}

/**
 * Audio library category
 */
export interface AudioCategory {
  id: string;
  name: string;
  description: string;
  files: AudioFileMetadata[];
}

// =============================================================================
// PLAYBACK STATE
// =============================================================================

/**
 * Playback status
 */
export type PlaybackStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'stopped';

/**
 * Complete playback state
 */
export interface PlaybackState {
  status: PlaybackStatus;
  currentMode: AudioMode | null;
  currentTime: number;
  duration: number;
  isLooping: boolean;
}

// =============================================================================
// VOLUME STATE
// =============================================================================

/**
 * Volume levels for all tracks
 */
export interface VolumeState {
  master: number;
  ambience: number;
  tone: number;
  voice: number;
}

/**
 * Default volume levels
 */
export const DEFAULT_VOLUMES: VolumeState = {
  master: 0.8,
  ambience: 0.7,
  tone: 0.3,
  voice: 0.9,
};

// =============================================================================
// ERROR TYPES
// =============================================================================

/**
 * Audio engine error codes
 */
export type AudioErrorCode = 
  | 'NOT_INITIALIZED'
  | 'CONTEXT_SUSPENDED'
  | 'LOAD_FAILED'
  | 'DECODE_FAILED'
  | 'PLAYBACK_FAILED'
  | 'NETWORK_ERROR'
  | 'UNSUPPORTED_FORMAT';

/**
 * Audio engine error
 */
export interface AudioError {
  code: AudioErrorCode;
  message: string;
  details?: unknown;
}

// =============================================================================
// EVENT TYPES
// =============================================================================

/**
 * Audio engine events
 */
export type AudioEngineEvent = 
  | 'initialized'
  | 'play'
  | 'pause'
  | 'stop'
  | 'trackLoaded'
  | 'trackEnded'
  | 'volumeChange'
  | 'modeChange'
  | 'error';

/**
 * Event payload types
 */
export interface AudioEngineEventPayloads {
  initialized: { sampleRate: number };
  play: { mode: AudioMode };
  pause: undefined;
  stop: undefined;
  trackLoaded: { trackId: TrackId; duration: number };
  trackEnded: { trackId: TrackId };
  volumeChange: { trackId: TrackId | 'master'; volume: number };
  modeChange: { mode: AudioMode };
  error: AudioError;
}

// =============================================================================
// UTILITY TYPES
// =============================================================================

/**
 * Volume value (0.0 - 1.0)
 */
export type VolumeValue = number;

/**
 * Frequency value in Hz
 */
export type FrequencyHz = number;

/**
 * Time value in seconds
 */
export type TimeSeconds = number;

/**
 * Time value in milliseconds
 */
export type TimeMs = number;